import { useEffect } from 'react';
import { useAuth } from '@/contexts/AuthContext';
import { supabase } from '@/integrations/supabase/client';

/**
 * On sign-in, ensure profiles.timezone matches the device's current IANA timezone.
 * Also "heals" when the user travels — updates on every app open if it changed.
 * Runs at most once per session per detected tz value.
 */
const LAST_TZ_KEY = 'pmc:profile_tz_last_synced';

export function useTimezoneCapture() {
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;
    const tz = Intl.DateTimeFormat().resolvedOptions().timeZone;
    if (!tz) return;
    const cacheKey = `${LAST_TZ_KEY}:${user.id}`;
    if (localStorage.getItem(cacheKey) === tz) return;

    (async () => {
      try {
        const { data: existing } = await supabase
          .from('profiles')
          .select('timezone')
          .eq('id', user.id)
          .maybeSingle();
        if ((existing as any)?.timezone === tz) {
          localStorage.setItem(cacheKey, tz);
          return;
        }
        const { error } = await supabase
          .from('profiles')
          .update({ timezone: tz } as never)
          .eq('id', user.id);
        if (!error) localStorage.setItem(cacheKey, tz);
      } catch {
        /* non-fatal */
      }
    })();
  }, [user]);
}
